import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Minus, Check, Timer, Pause, Play, Square, Repeat, Trash2 } from 'lucide-react';
import { doc, setDoc, getDoc, collection } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { Bar, ResponsiveContainer, Line, Legend, ComposedChart, XAxis, YAxis } from 'recharts';

import db, { auth } from '../../firebase';
import { getWorkout, getExerciseHistory, deleteExercise, SetRecording, Exercise } from '../../features/workoutSlice';
import LoadingSpinner from './LoadingSpinner';
import { Drawer, DrawerContent, DrawerTrigger, DrawerClose } from '../ui/drawer';

interface SetDetails {
  id: string;
  reps?: number;
  weight?: number;
  status?: 'completed' | 'incomplete';
  timeSeconds?: number;
}

const ExerciseTracking: React.FC = () => {
  const { workoutID, exerciseID } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = auth.currentUser;

  // Get cached data from WorkoutDetail page immediately
  const cachedAllSets = queryClient.getQueryData<Record<string, SetDetails[]>>(['allSetRecordings', workoutID]);
  const cachedSets = cachedAllSets?.[exerciseID!] || [];

  const [sets, setSets] = useState<SetDetails[]>(cachedSets);
  const [activeSetIndex, setActiveSetIndex] = useState(() => {
    if (cachedSets.length > 0) {
      const setParam = new URLSearchParams(window.location.search).get('set');
      if (setParam !== null) {
        const requestedIndex = parseInt(setParam, 10);
        if (requestedIndex >= 0 && requestedIndex < cachedSets.length) {
          return requestedIndex;
        }
      }
      const firstIncomplete = cachedSets.findIndex(s => s.status !== 'completed');
      return firstIncomplete >= 0 ? firstIncomplete : cachedSets.length - 1;
    }
    return 0;
  });
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerIsActive, setTimerIsActive] = useState(false);
  const [weightIncrement, setWeightIncrement] = useState(5);
  const [exerciseHistory, setExerciseHistory] = useState<SetRecording[]>([]);
  const hasInitialized = useRef(cachedSets.length > 0);
  const previousExerciseID = useRef(exerciseID);

  // Reset initialization when exerciseID changes
  useEffect(() => {
    if (previousExerciseID.current !== exerciseID) {
      previousExerciseID.current = exerciseID;

      // Get fresh cached data for the new exercise
      const freshCachedAllSets = queryClient.getQueryData<Record<string, SetDetails[]>>(['allSetRecordings', workoutID]);
      const freshCachedSets = freshCachedAllSets?.[exerciseID!] || [];

      setSets(freshCachedSets);
      hasInitialized.current = freshCachedSets.length > 0;

      if (freshCachedSets.length > 0) {
        const firstIncomplete = freshCachedSets.findIndex(s => s.status !== 'completed');
        setActiveSetIndex(firstIncomplete >= 0 ? firstIncomplete : freshCachedSets.length - 1);
      } else {
        setActiveSetIndex(0);
      }

      setTimerSeconds(0);
      setTimerIsActive(false);
    }
  }, [exerciseID, workoutID, queryClient]);

  const workoutQuery = useQuery({
    queryKey: ["workouts", user!.uid, "workout", workoutID],
    queryFn: () => getWorkout(workoutID!),
    enabled: !!workoutID,
    staleTime: 0,
  });

  const exercise = workoutQuery.data?.exercises?.find((ex: Exercise) => ex.pfxExerciseId === exerciseID);

  // Fetch set recordings - use cached data as initialData for instant display
  const fetchSetRecordings = async () => {
    if (workoutID && user && exerciseID) {
      const setRecordingsRef = doc(db, 'workouts', user.uid, 'workout', workoutID, 'set_recordings', exerciseID);
      const docSnap = await getDoc(setRecordingsRef);
      if (docSnap.exists()) {
        return docSnap.data().sets as SetDetails[];
      }
    }
    return [];
  };

  const { data: setRecordings } = useQuery({
    queryKey: ['setRecordings', workoutID, exerciseID],
    queryFn: fetchSetRecordings,
    enabled: !!workoutID && !!exerciseID,
    refetchOnWindowFocus: false,
    staleTime: 0,
    initialData: cachedSets.length > 0 ? cachedSets : undefined,
  });

  // Sync with server data if it differs from initial cached data
  useEffect(() => {
    if (setRecordings && !hasInitialized.current) {
      hasInitialized.current = true;
      setSets(setRecordings);

      if (setRecordings.length > 0) {
        const setParam = searchParams.get('set');
        if (setParam !== null) {
          const requestedIndex = parseInt(setParam, 10);
          if (requestedIndex >= 0 && requestedIndex < setRecordings.length) {
            setActiveSetIndex(requestedIndex);
            return;
          }
        }
        const firstIncompleteIndex = setRecordings.findIndex(s => s.status !== 'completed');
        setActiveSetIndex(firstIncompleteIndex >= 0 ? firstIncompleteIndex : setRecordings.length - 1);
      }
    }
  }, [setRecordings, searchParams]);

  useEffect(() => {
    const fetchHistory = async () => {
      if (workoutID && exerciseID) {
        const historyData = await getExerciseHistory(workoutID, exerciseID);
        setExerciseHistory(historyData);
      }
    };
    fetchHistory();
  }, [workoutID, exerciseID]);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (timerIsActive) {
      interval = setInterval(() => {
        setTimerSeconds(s => s + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timerIsActive]);

  const currentSet = sets[activeSetIndex] || { reps: 0, weight: 0 };

  const handleRepsChange = (delta: number) => {
    const newSets = [...sets];
    if (newSets[activeSetIndex]) {
      newSets[activeSetIndex] = {
        ...newSets[activeSetIndex],
        reps: Math.max(0, (newSets[activeSetIndex].reps || 0) + delta)
      };
      setSets(newSets);
    }
  };

  const handleWeightChange = (delta: number) => {
    const newSets = [...sets];
    if (newSets[activeSetIndex]) {
      newSets[activeSetIndex] = {
        ...newSets[activeSetIndex],
        weight: Math.max(0, (newSets[activeSetIndex].weight || 0) + delta)
      };
      setSets(newSets);
    }
  };

  const handleUsePrevious = () => {
    if (activeSetIndex > 0 && sets[activeSetIndex - 1]) {
      const prevSet = sets[activeSetIndex - 1];
      const newSets = [...sets];
      newSets[activeSetIndex] = {
        ...newSets[activeSetIndex],
        reps: prevSet.reps,
        weight: prevSet.weight
      };
      setSets(newSets);
    }
  };

  const handleRecordSet = async () => {
    const nextSetIndex = activeSetIndex < sets.length - 1 ? activeSetIndex + 1 : activeSetIndex;

    const newSets = sets.map((set, idx) =>
      idx === activeSetIndex ? { ...set, status: 'completed' as const, timeSeconds: timerSeconds } : set
    );

    // Update local state first
    setSets(newSets);
    setActiveSetIndex(nextSetIndex);
    setTimerSeconds(0);
    setTimerIsActive(false);

    // Update caches immediately for responsive UI
    queryClient.setQueryData(['allSetRecordings', workoutID], (old: Record<string, SetDetails[]> | undefined) => ({
      ...(old || {}),
      [exerciseID!]: newSets
    }));
    queryClient.setQueryData(['setRecordings', workoutID, exerciseID], newSets);

    // Save to server
    if (user && workoutID && exerciseID) {
      const setRecordingsRef = doc(db, 'workouts', user.uid, 'workout', workoutID, 'set_recordings', exerciseID);
      await setDoc(setRecordingsRef, { sets: newSets }, { merge: true });
    }
  };

  const handleAddSet = async () => {
    const newSet: SetDetails = { id: uuidv4(), reps: 0, weight: 0, status: 'incomplete', timeSeconds: 0 };
    const newSets = [...sets, newSet];

    // Update local state first
    setSets(newSets);
    setActiveSetIndex(newSets.length - 1);

    // Update caches immediately for responsive UI
    queryClient.setQueryData(['allSetRecordings', workoutID], (old: Record<string, SetDetails[]> | undefined) => ({
      ...(old || {}),
      [exerciseID!]: newSets
    }));
    queryClient.setQueryData(['setRecordings', workoutID, exerciseID], newSets);

    // Save to server
    if (user && workoutID && exerciseID) {
      const setRecordingsRef = doc(db, 'workouts', user.uid, 'workout', workoutID, 'set_recordings', exerciseID);
      await setDoc(setRecordingsRef, { sets: newSets }, { merge: true });
    }
  };

  const handleDeleteCurrentSet = async () => {
    if (sets.length === 0) return;

    const newSets = sets.filter((_, idx) => idx !== activeSetIndex);
    const newActiveIndex = Math.min(activeSetIndex, newSets.length - 1);

    // Update local state first
    setSets(newSets);
    setActiveSetIndex(Math.max(0, newActiveIndex));

    // Update caches immediately for responsive UI
    queryClient.setQueryData(['allSetRecordings', workoutID], (old: Record<string, SetDetails[]> | undefined) => ({
      ...(old || {}),
      [exerciseID!]: newSets
    }));
    queryClient.setQueryData(['setRecordings', workoutID, exerciseID], newSets);

    // Save to server
    if (user && workoutID && exerciseID) {
      const setRecordingsRef = doc(db, 'workouts', user.uid, 'workout', workoutID, 'set_recordings', exerciseID);
      await setDoc(setRecordingsRef, { sets: newSets }, { merge: true });
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDeleteExercise = async () => {
    if (workoutID && exerciseID) {
      await deleteExercise(workoutID, exerciseID);
      queryClient.invalidateQueries({ queryKey: ['workouts', user!.uid, 'workout', workoutID] });
      navigate(`/workouts/${workoutID}`);
    }
  };

  // Only show loading spinner on initial load when there's no cached data
  if (!workoutQuery.data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  const bodyParts = exercise?.bodyParts?.join(' · ') || '';

  return (
    <div className="flex flex-col pb-24 bg-background min-h-screen">
      {/* Header */}
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/workouts/${workoutID}`)}
              className="flex items-center justify-center w-8 h-8 rounded-2xl bg-secondary"
            >
              <ArrowLeft className="h-[18px] w-[18px] text-foreground" />
            </button>
            <div className="flex flex-col gap-0.5">
              <h1 className="text-lg font-bold text-foreground">{exercise?.name || 'Exercise'}</h1>
              <p className="text-xs text-muted-foreground">{bodyParts} · {sets.length} sets</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {sets.length > 0 && (
              <button
                onClick={handleDeleteCurrentSet}
                className="flex items-center justify-center w-8 h-8 rounded-2xl bg-destructive/10"
              >
                <Minus className="h-4 w-4 text-destructive" />
              </button>
            )}
            <button
              onClick={handleAddSet}
              className="flex items-center justify-center w-8 h-8 rounded-2xl"
              style={{ backgroundColor: 'rgba(99, 102, 241, 0.12)' }}
            >
              <Plus className="h-4 w-4" style={{ color: 'hsl(var(--tmw-accent))' }} />
            </button>
          </div>
        </div>
      </div>

      {/* Set Tabs */}
      <div className="flex gap-2 px-5 py-2">
        {sets.map((set, index) => {
          const isCompleted = set.status === 'completed';
          const isActive = index === activeSetIndex;
          const setTime = set.timeSeconds ? formatTime(set.timeSeconds) : null;

          return (
            <button
              key={set.id || index}
              onClick={() => setActiveSetIndex(index)}
              className="flex-1 min-h-10 py-1.5 rounded-[10px] flex flex-col items-center justify-center gap-0.5"
              style={{
                backgroundColor: isActive
                  ? 'hsl(var(--tmw-accent))'
                  : isCompleted
                    ? 'rgba(34, 197, 94, 0.12)'
                    : 'hsl(var(--secondary))',
                color: isActive
                  ? 'white'
                  : isCompleted
                    ? 'hsl(142 71% 45%)'
                    : 'hsl(var(--muted-foreground))'
              }}
            >
              <div className="flex items-center gap-1">
                {isCompleted && !isActive && <Check className="h-3 w-3" />}
                <span className="text-[13px] font-medium">Set {index + 1}</span>
              </div>
              {isCompleted && setTime && (
                <span className="text-[10px] opacity-80">{setTime}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Data Section */}
      <div className="flex gap-3 px-5 py-3">
        {/* Reps */}
        <div className="flex-1 flex flex-col items-center gap-1">
          <span className="text-[11px] font-semibold text-muted-foreground">REPS</span>
          <span className="text-5xl font-extrabold text-foreground">{currentSet.reps || 0}</span>
          <div className="flex items-center gap-3 mt-1">
            <button
              onClick={() => handleRepsChange(-1)}
              className="flex items-center justify-center w-8 h-8 rounded-2xl bg-secondary"
            >
              <Minus className="h-4 w-4 text-muted-foreground" />
            </button>
            <button
              onClick={() => handleRepsChange(1)}
              className="flex items-center justify-center w-8 h-8 rounded-2xl bg-secondary"
            >
              <Plus className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Weight */}
        <div className="flex-1 flex flex-col items-center gap-1">
          <span className="text-[11px] font-semibold text-muted-foreground">WEIGHT</span>
          <div className="flex items-end gap-1">
            <span className="text-5xl font-extrabold text-foreground">{currentSet.weight || 0}</span>
            <span className="text-base font-medium text-muted-foreground mb-2">lbs</span>
          </div>
          <div className="flex items-center gap-4 mt-1">
            <button
              onClick={() => handleWeightChange(-weightIncrement)}
              className="flex items-center justify-center w-8 h-8 rounded-2xl bg-secondary"
            >
              <Minus className="h-4 w-4 text-muted-foreground" />
            </button>
            <Drawer>
              <DrawerTrigger asChild>
                <button className="px-2 py-1 rounded-lg bg-secondary/50">
                  <span className="text-[11px] font-medium" style={{ color: 'hsl(var(--tmw-accent))' }}>±{weightIncrement}</span>
                </button>
              </DrawerTrigger>
              <DrawerContent className="p-4">
                <h3 className="text-lg font-semibold text-foreground mb-4">Weight Increment</h3>
                <div className="grid grid-cols-4 gap-2">
                  {[1, 2.5, 5, 10, 15, 20, 25, 45].map((increment) => (
                    <DrawerClose key={increment} asChild>
                      <button
                        onClick={() => setWeightIncrement(increment)}
                        className="h-12 rounded-xl flex items-center justify-center text-sm font-semibold transition-colors"
                        style={{
                          backgroundColor: weightIncrement === increment ? 'hsl(var(--tmw-accent))' : 'hsl(var(--secondary))',
                          color: weightIncrement === increment ? 'white' : 'hsl(var(--foreground))'
                        }}
                      >
                        {increment}
                      </button>
                    </DrawerClose>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground text-center mt-4">Select increment in lbs</p>
              </DrawerContent>
            </Drawer>
            <button
              onClick={() => handleWeightChange(weightIncrement)}
              className="flex items-center justify-center w-8 h-8 rounded-2xl bg-secondary"
            >
              <Plus className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>

      {/* Timer Section */}
      <div className="flex items-center justify-between px-5 py-3">
        <div className="flex items-center gap-2.5">
          <Timer className="h-5 w-5" style={{ color: 'hsl(var(--tmw-accent))' }} />
          <span className="text-[28px] font-bold text-foreground font-mono">{formatTime(timerSeconds)}</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setTimerIsActive(!timerIsActive)}
            className="flex items-center justify-center w-10 h-10 rounded-full"
            style={{ backgroundColor: timerIsActive ? 'hsl(38 92% 50%)' : 'hsl(142 71% 45%)' }}
          >
            {timerIsActive ? (
              <Pause className="h-[18px] w-[18px] text-white" />
            ) : (
              <Play className="h-[18px] w-[18px] text-white" />
            )}
          </button>
          <button
            onClick={() => { setTimerSeconds(0); setTimerIsActive(false); }}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-destructive"
          >
            <Square className="h-[18px] w-[18px] text-white" />
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 px-5 py-1">
        <button
          onClick={handleUsePrevious}
          className="flex-1 flex items-center justify-center gap-1.5 h-[42px] rounded-[10px] bg-secondary"
        >
          <Repeat className="h-3.5 w-3.5 text-foreground" />
          <span className="text-xs font-medium text-foreground">Use Previous</span>
        </button>
        <button
          onClick={handleRecordSet}
          className="flex-1 flex items-center justify-center gap-1.5 h-[42px] rounded-[10px]"
          style={{ backgroundColor: 'hsl(var(--tmw-accent))' }}
        >
          <Check className="h-3.5 w-3.5 text-white" />
          <span className="text-xs font-semibold text-white">Record Set</span>
        </button>
      </div>

      {/* Exercise History */}
      {exerciseHistory.length > 0 && (
        <div className="px-5 pt-3 pb-5">
          <p className="text-[13px] font-semibold text-muted-foreground mb-2.5">EXERCISE HISTORY</p>
          <div className="flex gap-4 mb-3">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: 'hsl(var(--tmw-accent))' }} />
              <span className="text-[11px] text-muted-foreground">Reps</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'hsl(38 92% 50%)' }} />
              <span className="text-[11px] text-muted-foreground">Weight (lbs)</span>
            </div>
          </div>
          <div className="h-[160px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={exerciseHistory.sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()).slice(-10)} margin={{ top: 20, right: 5, left: 5, bottom: 5 }}>
                <XAxis
                  dataKey="createdAt"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                  tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Bar dataKey="reps" fill="hsl(var(--tmw-accent))" opacity={0.25} radius={[3, 3, 0, 0]} label={{ position: 'top', fontSize: 10, fill: 'hsl(var(--tmw-accent))' }} />
                <Line type="monotone" dataKey="weight" stroke="hsl(38 92% 50%)" strokeWidth={2} dot={false} label={{ position: 'top', fontSize: 10, fill: 'hsl(38 92% 50%)', offset: 8 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Delete Exercise */}
      <div className="fixed bottom-0 left-0 right-0 px-5 pb-8 pt-3 bg-background">
        <button
          onClick={handleDeleteExercise}
          className="w-full flex items-center justify-center gap-2 h-11 rounded-xl bg-destructive/10"
        >
          <Trash2 className="h-4 w-4 text-destructive" />
          <span className="text-sm font-medium text-destructive">Delete Exercise</span>
        </button>
      </div>
    </div>
  );
};

export default ExerciseTracking;
